import React from 'react';
import moment from 'moment';
import { getWeather, doesWeatherValueExist, getWeatherIconStates } from '../../../../utils/functions';

import WindCompass from '../../../../components/WindCompass';
import LightningRadar, { LightningLegend } from '../../../../components/LightningRadar';
import WeatherStatusIcons from '../../../../components/WeatherStatusIcons';

moment.tz.setDefault('UTC');

const IWOSDashboard = ({
	latestEntry,
	isLightningActive,
	isRainActive,
	isCloudsActive,
	isTamperingActive,
	batteryLevel,
	showMetricOnly,
	theme = 'dark',
}) => {
	// Helper functions
	const getValue = (field, suffix = '') => {
		if (!doesWeatherValueExist(field)) return '-';
		return `${field.value}${suffix}`;
	};

	const getNumericValue = (field) => {
		if (!doesWeatherValueExist(field)) return null;
		const val = parseFloat(field.value);
		return isNaN(val) ? null : val;
	};

	// Temperature data
	const tempC = getNumericValue(latestEntry.temperatureC);
	const tempF = getNumericValue(latestEntry.temperatureF);
	const tempMinC = getNumericValue(latestEntry.tempMin24hrC);
	const tempMinF = getNumericValue(latestEntry.tempMin24hrF);
	const tempMaxC = getNumericValue(latestEntry.tempMax24hrC);
	const tempMaxF = getNumericValue(latestEntry.tempMax24hrF);
	const dewC = getNumericValue(latestEntry.dewPoint);
	const dewF = getNumericValue(latestEntry.dewPointF);
	const humidity = getValue(latestEntry.humidity, '%');
	const heatIndexC = getNumericValue(latestEntry.heatIndexC);
	const heatIndexF = getNumericValue(latestEntry.heatIndexF);
	const windChillC = getNumericValue(latestEntry.windChillC);
	const windChillF = getNumericValue(latestEntry.windChillF);

	// Wind data (2-min average)
	const windDir2min = getNumericValue(latestEntry.windDir2min);
	const windSpeed2min = getNumericValue(latestEntry.windSpeed2minKt);

	// Gust data (with fallback for legacy field name)
	const gustDir = getNumericValue(latestEntry.gustDir);
	const gustSpeed = getNumericValue(latestEntry.gustSpeedKt) ?? getNumericValue(latestEntry.gustMagnitude);

	// 10-min wind history
	const windDir10minMin = getValue(latestEntry.windDir10minMin, '°');
	const windDir10minMax = getValue(latestEntry.windDir10minMax, '°');
	const windSpeed10minMinKt = latestEntry.windSpeed10minMinKt?.value
		? `${latestEntry.windSpeed10minMinKt.value} kt`
		: '-';
	const windSpeed10minMaxKt = latestEntry.windSpeed10minMaxKt?.value
		? `${latestEntry.windSpeed10minMaxKt.value} kt`
		: '-';

	// 1-hr wind history
	const windDir1hrMin = getValue(latestEntry.windDir1hrMin, '°');
	const windDir1hrMax = getValue(latestEntry.windDir1hrMax, '°');
	const windSpeed1hrMinKt = latestEntry.windSpeed1hrMinKt?.value
		? `${latestEntry.windSpeed1hrMinKt.value} kt`
		: '-';
	const windSpeed1hrMaxKt = latestEntry.windSpeed1hrMaxKt?.value
		? `${latestEntry.windSpeed1hrMaxKt.value} kt`
		: '-';

	// Pressure data
	const altimeter = getValue(latestEntry.altimeter, ' inHg');
	const altimeterMb = getValue(latestEntry.altimeterMb, ' hPa');
	const stationPressure = latestEntry.haPressureInHg?.value && latestEntry.haPressureInHg.value !== '0'
		? `${latestEntry.haPressureInHg.value} inHg`
		: getValue(latestEntry.stationPressure, ' inHg');
	const stationPressureMb = getValue(latestEntry.stationPressureMb, ' hPa');
	const slp = getValue(latestEntry.slp, ' hPa');
	const pressureAltFt = getValue(latestEntry.pressureAltFt, ' ft');
	const pressureAltM = getValue(latestEntry.pressureAlt, ' m');
	const denseAltFt = getValue(latestEntry.denseAltFt, ' ft');
	const denseAltM = getValue(latestEntry.denseAlt, ' m');
	// Pressure tendency (with fallback for legacy field name)
	const pressureTendency = doesWeatherValueExist(latestEntry.pressureTendency3hr)
		? getValue(latestEntry.pressureTendency3hr, ' hPa')
		: getValue(latestEntry.pressureDiff3hr, ' hPa');

	// Lightning data (1/10/60 min)
	const lightningFreq1 = getNumericValue(latestEntry.lightningFreq) || 0;
	const lightningDist1 = getNumericValue(latestEntry.lightningDist);
	const lightningFreq10 = getNumericValue(latestEntry.lightningFreq10) || 0;
	const lightningDist10 = getNumericValue(latestEntry.lightningDist10);
	const lightningFreq60 = getNumericValue(latestEntry.lightningFreq60) || 0;
	const lightningDist60 = getNumericValue(latestEntry.lightningDist60);

	// Visibility data (with fallback for legacy field name case)
	const visibility1min = doesWeatherValueExist(latestEntry.visibility1min)
		? getValue(latestEntry.visibility1min, ' SM')
		: getValue(latestEntry.visibility1Min, ' SM');
	const visibility1minM = doesWeatherValueExist(latestEntry.visibility1minM)
		? getValue(latestEntry.visibility1minM, ' m')
		: getValue(latestEntry.visibilityMeters, ' m');
	const visibility10min = getValue(latestEntry.visibility, ' SM');
	const visibility10minM = getValue(latestEntry.visibilityM, ' m');

	// Cloud layers - parse into 6 individual layer values
	const parseCloudLayers = () => {
		const layers = ['-', '-', '-', '-', '-', '-'];
		if (!doesWeatherValueExist(latestEntry.cloudLayers)) return layers;

		const value = latestEntry.cloudLayers.value;
		// Handle special cases
		if (value === 'NCD' || value === 'CLR' || value === 'No Ceilometer' || value === 'Ceilometer OFF') {
			return layers;
		}

		// Parse comma-separated values (in meters)
		const layerValues = value.split(',').map(v => v.trim()).filter(v => v);
		layerValues.forEach((layerM, index) => {
			if (index < 6) {
				const meters = parseFloat(layerM);
				if (!isNaN(meters)) {
					// Convert meters to feet
					const feet = Math.round(meters * 3.28084);
					layers[index] = `${feet} ft`;
				}
			}
		});
		return layers;
	};
	const cloudLayerValues = parseCloudLayers();

	// Present Weather
	const presentWeatherCode = latestEntry.weather?.value;
	const presentWeatherText = presentWeatherCode
		? getWeather(latestEntry.weather)
		: '-';

	// Precipitation accumulations
	const precipAcc1hr = showMetricOnly
		? getValue(latestEntry.precipAcc1hrMm, ' mm')
		: getValue(latestEntry.precipAcc1hr, ' in');
	const precipAcc3hr = showMetricOnly
		? getValue(latestEntry.precipAcc3hrMm, ' mm')
		: getValue(latestEntry.precipAcc3hr, ' in');
	const precipAcc6hr = showMetricOnly
		? getValue(latestEntry.precipAcc6hrMm, ' mm')
		: getValue(latestEntry.precipAcc6hr, ' in');
	const precipAcc24hr = showMetricOnly
		? getValue(latestEntry.precipAcc24hrMm, ' mm')
		: getValue(latestEntry.precipAcc24hr, ' in');

	// Weather conditions for status icons — use raw WMO code (pwdWMO) for reliable matching
	// Falls back to description string which now handles word-boundary matching
	const weatherStates = getWeatherIconStates(latestEntry.pwdWMO?.value || presentWeatherCode);
	const obscurationStates = getWeatherIconStates(latestEntry.obscuration?.value);
	const isHazeActive = weatherStates.haze || obscurationStates.haze;
	const isFogActive = weatherStates.fog || obscurationStates.fog;
	const isDrizzleActive = weatherStates.drizzle;
	const isHailActive = weatherStates.hail;
	const isFreezingRainActive = weatherStates.freezingRain;
	const isSnowActive = weatherStates.snow;
	const isRainFromWeather = weatherStates.rain;

	// Only use sensor-based rain if weather code doesn't indicate another precipitation type
	// (e.g., if it's snowing, don't show rain just because precipitation sensor detects moisture)
	const hasSpecificPrecipType = isSnowActive || isDrizzleActive || isFreezingRainActive || isHailActive;
	const effectiveRainActive = isRainFromWeather || (isRainActive && !hasSpecificPrecipType);

	return (
		<div className="dashboard-grid">
			{/* LEFT COLUMN */}
			{/* Timestamp Section */}
			<div className="dashboard-section timestamp-section">
				<div className="section-header">Last Observation</div>
				<div className="section-content timestamp-content">
					<div className="timestamp-row">
						<span className="timestamp-label">PC Time (UTC)</span>
						<span className="timestamp-value">
							{moment().utc().format('YY-MM-DD HH:mm:ss')}
						</span>
					</div>
					<div className="timestamp-row">
						<span className="timestamp-label">Device</span>
						<span className="timestamp-value">
							{moment(latestEntry.timestamp).format('YY-MM-DD HH:mm:ss')}
						</span>
					</div>
				</div>
			</div>

			{/* Status Section */}
			<div className="dashboard-section status-section">
				<div className="section-header">Status</div>
				<div className="section-content">
					<WeatherStatusIcons
						theme={theme}
						isHazeActive={isHazeActive}
						isFogActive={isFogActive}
						isDrizzleActive={isDrizzleActive}
						isRainActive={effectiveRainActive}
						isHailActive={isHailActive}
						isFreezingRainActive={isFreezingRainActive}
						isSnowActive={isSnowActive}
						isLightningActive={isLightningActive}
						isTamperingActive={isTamperingActive}
						batteryLevel={batteryLevel}
					/>
				</div>
			</div>

			{/* CENTER COLUMN */}
			{/* Wind Section */}
			<div className="dashboard-section wind-section">
				<div className="section-header">Wind</div>
				<div className="section-content wind-content">
					{/* Top row: Compass + Current + 2min avg */}
					<div className="wind-top-row">
						<div className="wind-compass-area">
							<WindCompass
								direction={windDir2min}
								theme={theme}
								size={160}
							/>
						</div>
						<div className="wind-current">
							<h4>Current</h4>
							<table className="dashboard-table">
								<tbody>
									<tr>
										<td>Direction</td>
										<td className="value-cell">{windDir2min !== null ? windDir2min : '-'}</td>
										<td className="unit-cell">°</td>
									</tr>
									<tr>
										<td>Speed</td>
										<td className="value-cell">{windSpeed2min ?? '-'}</td>
										<td className="unit-cell">kt</td>
									</tr>
									<tr>
										<td>Gust Direction</td>
										<td className="value-cell">{gustDir ?? '-'}</td>
										<td className="unit-cell">°</td>
									</tr>
									<tr>
										<td>Gust Speed</td>
										<td className="value-cell">{gustSpeed ?? '-'}</td>
										<td className="unit-cell">kt</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div className="wind-2min-avg">
							<h4>2 minute average</h4>
							<table className="dashboard-table">
								<tbody>
									<tr>
										<td>Direction</td>
										<td className="value-cell">{windDir2min !== null ? windDir2min : '-'}</td>
										<td className="unit-cell">°</td>
									</tr>
									<tr>
										<td>Speed</td>
										<td className="value-cell">{windSpeed2min ?? '-'}</td>
										<td className="unit-cell">kt</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					{/* History section */}
					<div className="wind-history-section">
						<h4 className="history-title">HISTORY</h4>
						<div className="history-divider"></div>
						<div className="history-tables">
							<div className="history-table">
								<h5>10 Minute</h5>
								<table className="dashboard-table">
									<thead>
										<tr>
											<th></th>
											<th>Min</th>
											<th>Max</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td>Direction</td>
											<td className="value-cell">{windDir10minMin}°</td>
											<td className="value-cell">{windDir10minMax}°</td>
										</tr>
										<tr>
											<td>Speed</td>
											<td className="value-cell">{windSpeed10minMinKt} kt</td>
											<td className="value-cell">{windSpeed10minMaxKt} kt</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div className="history-table">
								<h5>1 Hour</h5>
								<table className="dashboard-table">
									<thead>
										<tr>
											<th></th>
											<th>Min</th>
											<th>Max</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td>Direction</td>
											<td className="value-cell">{windDir1hrMin}°</td>
											<td className="value-cell">{windDir1hrMax}°</td>
										</tr>
										<tr>
											<td>Speed</td>
											<td className="value-cell">{windSpeed1hrMinKt} kt</td>
											<td className="value-cell">{windSpeed1hrMaxKt} kt</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Lightning Section */}
			<div className="dashboard-section lightning-section">
				<div className="section-header">Lightning</div>
				<div className="section-content lightning-content">
					<div className="lightning-radar-area">
						<LightningRadar
							theme={theme}
							size={160}
							strikeCount1min={lightningFreq1}
							strikeDist1min={lightningDist1}
							strikeCount10min={lightningFreq10}
							strikeDist10min={lightningDist10}
							strikeCount60min={lightningFreq60}
							strikeDist60min={lightningDist60}
						/>
					</div>
					<div className="lightning-legend-area">
						<LightningLegend />
						<p className="lightning-range-text">Range Rings: 5, 10, 25, 50 NM</p>
					</div>
					<div className="lightning-data-area">
						<table className="dashboard-table">
							<thead>
								<tr>
									<th></th>
									<th>Strike Count</th>
									<th>Min Distance</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>1 Min</td>
									<td className="value-cell">{lightningFreq1}</td>
									<td className="value-cell">{lightningDist1 !== null ? `${lightningDist1} NM` : '-'}</td>
								</tr>
								<tr>
									<td>10 Min</td>
									<td className="value-cell">{lightningFreq10}</td>
									<td className="value-cell">{lightningDist10 !== null ? `${lightningDist10} NM` : '-'}</td>
								</tr>
								<tr>
									<td>60 Min</td>
									<td className="value-cell">{lightningFreq60}</td>
									<td className="value-cell">{lightningDist60 !== null ? `${lightningDist60} NM` : '-'}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Weather & Clouds Row (side by side) */}
			<div className="weather-clouds-row">
				{/* Weather Section */}
				<div className="dashboard-section weather-section">
					<div className="section-header">Weather</div>
					<div className="section-content">
						<div className="card-rows">
							<div className="card-row present-weather-row">
								<span className="row-label">Present Weather</span>
								<span className="row-value">{presentWeatherText}</span>
							</div>
							<div className="card-row">
								<span className="row-label">1 hr Precipitation</span>
								<span className="row-value">{precipAcc1hr}</span>
							</div>
							<div className="card-row">
								<span className="row-label">3 hr Precipitation</span>
								<span className="row-value">{precipAcc3hr}</span>
							</div>
							<div className="card-row">
								<span className="row-label">6 hr Precipitation</span>
								<span className="row-value">{precipAcc6hr}</span>
							</div>
							<div className="card-row">
								<span className="row-label">24 hr Precipitation</span>
								<span className="row-value">{precipAcc24hr}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Clouds Section */}
				<div className="dashboard-section clouds-section">
					<div className="section-header">Clouds</div>
					<div className="section-content">
						<div className="card-rows">
							<div className="card-row">
								<span className="row-label">Layer 1</span>
								<span className="row-value">{cloudLayerValues[0]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 2</span>
								<span className="row-value">{cloudLayerValues[1]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 3</span>
								<span className="row-value">{cloudLayerValues[2]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 4</span>
								<span className="row-value">{cloudLayerValues[3]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 5</span>
								<span className="row-value">{cloudLayerValues[4]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 6</span>
								<span className="row-value">{cloudLayerValues[5]}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* RIGHT COLUMN */}
			{/* Temperature Section */}
			<div className="dashboard-section temperature-section">
				<div className="section-header">Temperature</div>
				<div className="section-content">
					<div className="table-responsive">
						<table className="dashboard-table">
							<thead>
								<tr>
									<th></th>
									<th>Current</th>
									<th>24hr min</th>
									<th>24hr max</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Temperature</td>
									<td className="value-cell">{showMetricOnly ? (tempC ?? '-') : (tempF ?? '-')}</td>
									<td className="value-cell">{showMetricOnly ? (tempMinC ?? '-') : (tempMinF ?? '-')}</td>
									<td className="value-cell">{showMetricOnly ? (tempMaxC ?? '-') : (tempMaxF ?? '-')}</td>
									<td className="unit-cell">{showMetricOnly ? '°C' : '°F'}</td>
								</tr>
								<tr>
									<td>Dew Point</td>
									<td className="value-cell">{showMetricOnly ? (dewC ?? '-') : (dewF ?? '-')}</td>
									<td className="value-cell"></td>
									<td className="value-cell"></td>
									<td className="unit-cell">{showMetricOnly ? '°C' : '°F'}</td>
								</tr>
								<tr>
									<td>Relative Humidity</td>
									<td className="value-cell">{humidity?.replace('%', '') ?? '-'}</td>
									<td className="value-cell"></td>
									<td className="value-cell"></td>
									<td className="unit-cell">%</td>
								</tr>
								<tr>
									<td>Heat Index</td>
									<td className="value-cell">{showMetricOnly ? (heatIndexC ?? '-') : (heatIndexF ?? '-')}</td>
									<td className="value-cell"></td>
									<td className="value-cell"></td>
									<td className="unit-cell">{showMetricOnly ? '°C' : '°F'}</td>
								</tr>
								<tr>
									<td>Wind Chill</td>
									<td className="value-cell">{showMetricOnly ? (windChillC ?? '-') : (windChillF ?? '-')}</td>
									<td className="value-cell"></td>
									<td className="value-cell"></td>
									<td className="unit-cell">{showMetricOnly ? '°C' : '°F'}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Pressure Section */}
			<div className="dashboard-section pressure-section">
				<div className="section-header">Pressure</div>
				<div className="section-content">
					<div className="table-responsive">
						<table className="dashboard-table no-header">
							<tbody>
								<tr>
									<td>Altimeter</td>
									<td className="value-cell">{altimeter}</td>
									<td className="value-cell">{altimeterMb}</td>
								</tr>
								<tr>
									<td>Station</td>
									<td className="value-cell">{stationPressure}</td>
									<td className="value-cell">{stationPressureMb}</td>
								</tr>
								<tr>
									<td>SLP</td>
									<td className="value-cell"></td>
									<td className="value-cell">{slp}</td>
								</tr>
								<tr>
									<td>PA</td>
									<td className="value-cell">{pressureAltFt}</td>
									<td className="value-cell">{pressureAltM}</td>
								</tr>
								<tr>
									<td>DA</td>
									<td className="value-cell">{denseAltFt}</td>
									<td className="value-cell">{denseAltM}</td>
								</tr>
								<tr>
									<td>3hr</td>
									<td className="value-cell">{pressureTendency}</td>
									<td className="value-cell"></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Visibility Section */}
			<div className="dashboard-section visibility-section">
				<div className="section-header">Visibility</div>
				<div className="section-content">
					<div className="table-responsive">
						<table className="dashboard-table no-header">
							<tbody>
								<tr>
									<td>1 min Vis</td>
									<td className="value-cell">{visibility1min ? `${visibility1min} SM` : '-'}</td>
									<td className="value-cell">{visibility1minM ? `${visibility1minM} m` : '-'}</td>
								</tr>
								<tr>
									<td>10 min Vis</td>
									<td className="value-cell">{visibility10min ? `${visibility10min} SM` : '-'}</td>
									<td className="value-cell">{visibility10minM ? `${visibility10minM} m` : '-'}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
};

export default IWOSDashboard;
